import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { findFirstPartyMcpBySlug } from '@wix/ai-scheduling-assistant-constants';
import { useGenericBi } from './use-generic-bi';
import { useTranslation } from './i18n';
import { useProductName } from './use-brand';
import { CONNECTORS_PAGE } from './connectors-bi';
import { connectorConnectRoute } from './connector-connect-route';
import {
  authorizeConnector,
  calendarProviderToProviderId,
  disconnectCalendarAccounts,
  disconnectConnector,
  fetchConnectors,
  type ConnectorStatus,
} from './connectors';
import { PROVIDERS, useCalendarConnect, type Provider } from '../components/CalendarConnect';
import { chatHandoffLink } from './chat-handoff';
import { deleteMcpConnection } from './mcp';
import { useBasePath } from './use-base-path';

// The connect/disconnect RAIL DISPATCH, extracted so both the Connectors page (via useAppsController)
// and the empty-chat suggested-task CONNECT chip share ONE implementation of it. There is exactly one
// `connectorConnectRoute` branch in the app, here — a chip that "connects" must run the same
// composio/native-calendar/assistant-chat flow the Connectors page does, not a second copy of it.
//
// It owns the connect subsystem's state (busy/error/the inline Apple form) and the popup + poll timers,
// but NOT the connectors list itself: the caller keeps that (useAppsController in local state, the chip
// in the shared React Query cache) and hands this hook `getConnectors` to read the add-another baseline
// and `refresh` to re-read after a connect. `onConnected` arms the assistant-wake (see lib/connect-handoff)
// — the chat surface passes it so the genome's turn is awaited; the Connectors page omits it.

// ACTIVE accounts on a row — drives add-another success detection (count rising over the pre-popup
// baseline, covering both first connect 0→1 and add-another 1→2). Falls back to the connected boolean.
export function accountCountFor(connector: ConnectorStatus | undefined): number {
  return connector?.accounts?.length ?? (connector?.connected ? 1 : 0);
}

// Composio lands its OAuth callback on its own servers — the popup never
// messages back — so polling status is the only signal a connect completed.
const POLL_INTERVAL_MS = 1500;
const MAX_POLL_ATTEMPTS = 80;

export interface ConnectConnector {
  busy: string | null;
  actionError: string | null;
  // Apple connects with an app-specific password (not OAuth) — its card expands an inline form.
  icloudOpenFor: string | null;
  appleId: string;
  applePassword: string;
  setAppleId: (v: string) => void;
  setApplePassword: (v: string) => void;
  submitICloud: () => void;
  connect: (connector: ConnectorStatus) => void;
  addAnother: (connector: ConnectorStatus) => void;
  disconnect: (connector: ConnectorStatus, name: string, accountId?: string) => void;
}

export function useConnectConnector({
  getConnectors,
  getGmailScopesEnabled,
  refresh,
  onConnected,
}: {
  /** The current connectors snapshot — read at connect time for the add-another baseline. */
  getConnectors: () => ConnectorStatus[];
  /** Whether a Google connect should also ask for the Gmail scopes, per the server's default-off gate.
   *  Omitted ⇒ calendar scopes only, which is the right answer for any surface that has not read the
   *  status: the Gmail scopes are unapproved, and consenting to one spends an unrecoverable cap slot. */
  getGmailScopesEnabled?: () => boolean;
  /** Re-read the connectors list into whatever store the caller keeps it in (local state / RQ cache). */
  refresh: () => Promise<void>;
  /** Arm the assistant-wake after a successful connect, with the connector's display name (drives the
   *  "Inspecting your {name}." handoff placeholder). The chat surface passes `noteConnected`; the
   *  Connectors page omits it (no conversation to wake). */
  onConnected?: (name: string) => void;
}): ConnectConnector {
  const productName = useProductName();
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const popupRef = useRef<Window | null>(null);
  // Native calendar rows reuse the settings/onboarding calendar plumbing: OAuth popup for
  // Google/Outlook, the inline app-password form for Apple.
  const { connectOAuth, connectICloud } = useCalendarConnect({ getGmailScopesEnabled });
  const [icloudOpenFor, setIcloudOpenFor] = useState<string | null>(null);
  const [appleId, setAppleId] = useState('');
  const [applePassword, setApplePassword] = useState('');
  // First-party MCP rows (WordPress) connect via a chat handoff (the assistant runs the OAuth dance
  // through ManageMcp) — needs the router + mount prefix to build the handoff link imperatively.
  const router = useRouter();
  const basePath = useBasePath();

  // Read the caller's inputs through refs so the connect callbacks keep stable identities regardless of
  // how the caller sources the connectors list — the click handlers below are never in a fetching effect,
  // but a stable identity keeps them cheap to pass down and impossible to couple into one.
  const getConnectorsRef = useRef(getConnectors);
  getConnectorsRef.current = getConnectors;
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const onConnectedRef = useRef(onConnected);
  onConnectedRef.current = onConnected;

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      popupRef.current?.close();
    };
  }, []);

  // Resolve once the toolkit's ACTIVE account count rises above `baseline` — works for a first connect
  // (0→1) and for adding another account (1→2) alike.
  const pollUntilAccountAdded = useCallback((toolkit: string, baseline: number, popup: Window) => {
    return new Promise<void>((resolve, reject) => {
      let attempts = 0;
      let inFlight = false;
      const timer = setInterval(async () => {
        if (popup.closed) {
          clearInterval(timer);
          pollTimerRef.current = null;
          reject(new Error('popup_closed'));
          return;
        }
        if (inFlight) return;
        attempts += 1;
        if (attempts > MAX_POLL_ATTEMPTS) {
          clearInterval(timer);
          pollTimerRef.current = null;
          resolve();
          return;
        }
        inFlight = true;
        try {
          const { connectors } = await fetchConnectors();
          if (accountCountFor(connectors.find((c) => c.toolkit === toolkit)) > baseline) {
            clearInterval(timer);
            pollTimerRef.current = null;
            resolve();
          }
        } catch {
          // transient read failure — keep polling within the cap
        } finally {
          inFlight = false;
        }
      }, POLL_INTERVAL_MS);
      pollTimerRef.current = timer;
    });
  }, []);

  // Native calendar connect: OAuth popup (Google/Outlook) returns to chat.nownia.com which postMessages
  // success; Apple expands an inline app-password form instead. Refreshes the list on success.
  const connectNative = useCallback(
    async (connector: ConnectorStatus) => {
      if (busy) return;
      const providerId = calendarProviderToProviderId(connector.calendarProvider);
      if (providerId === 'apple') {
        setActionError(null);
        setIcloudOpenFor((open) => (open === connector.toolkit ? null : connector.toolkit));
        return;
      }
      const provider = PROVIDERS.find((p): p is Provider => p.id === providerId);
      if (!provider || provider.kind !== 'oauth') {
        setActionError("Couldn't start the calendar connect. Please try again.");
        return;
      }
      setIcloudOpenFor(null); // close a lingering Apple form when connecting a different provider
      setBusy(connector.toolkit);
      setActionError(null);
      try {
        const { connected, errorKind } = await connectOAuth(provider);
        if (connected) {
          await refreshRef.current();
          onConnectedRef.current?.(connector.name ?? '');
        } else if (errorKind === 'missing_permission') {
          // The one failure the user can act on: Google let them finish the flow without ticking the
          // calendar permission, so the row has to say which permission to allow rather than nothing.
          setActionError(t('calendar.connect.error.missingPermission'));
        }
      } catch {
        setActionError("Couldn't reach the calendar service. Please try again.");
      } finally {
        setBusy(null);
      }
    },
    [busy, connectOAuth, t],
  );

  const submitICloud = useCallback(async () => {
    if (busy) return;
    setBusy('calendar-apple');
    setActionError(null);
    try {
      if (await connectICloud(appleId, applePassword)) {
        const appleName = getConnectorsRef.current().find((c) => c.toolkit === icloudOpenFor)?.name ?? '';
        setIcloudOpenFor(null);
        setAppleId('');
        setApplePassword('');
        await refreshRef.current();
        onConnectedRef.current?.(appleName);
      }
    } catch {
      setActionError("Couldn't connect that Apple account. Please try again.");
    } finally {
      setBusy(null);
    }
  }, [busy, connectICloud, appleId, applePassword, icloudOpenFor]);

  // Composio connect — first connect, or "add another account" (allowMultiple). Opens the OAuth popup
  // and polls until the toolkit's account count rises over its pre-popup baseline.
  const connectComposio = useCallback(
    async (toolkit: string, allowMultiple = false) => {
      if (busy) return;
      setIcloudOpenFor(null); // close a lingering Apple form when connecting a Composio app
      logPageClickBi({ ...CONNECTORS_PAGE, button_name: `${allowMultiple ? 'add_another' : 'connect'}_${toolkit}` });
      setBusy(toolkit);
      setActionError(null);
      const baseline = accountCountFor(getConnectorsRef.current().find((c) => c.toolkit === toolkit));
      let popup: Window | null = null;
      try {
        const result = await authorizeConnector(toolkit, allowMultiple);
        if (result.alreadyConnected) {
          await refreshRef.current();
          return;
        }
        if (!result.url) {
          throw new Error(result.error || 'no_auth_url');
        }
        popup = window.open(result.url, 'orion-connector-oauth', 'width=520,height=720');
        popupRef.current = popup;
        if (!popup) {
          throw new Error('popup_blocked');
        }
        await pollUntilAccountAdded(toolkit, baseline, popup);
        await refreshRef.current();
        onConnectedRef.current?.(getConnectorsRef.current().find((c) => c.toolkit === toolkit)?.name ?? '');
      } catch (err) {
        const message = err instanceof Error ? err.message : '';
        if (message === 'popup_blocked') {
          setActionError("Couldn't open the connect window. Check your browser's popup blocker.");
        } else if (message === 'popup_closed') {
          await refreshRef.current();
        } else {
          setActionError("Couldn't reach the connector service. Please try again.");
        }
      } finally {
        popup?.close();
        popupRef.current = null;
        setBusy(null);
      }
    },
    [busy, logPageClickBi, pollUntilAccountAdded],
  );

  // First-party MCP connect: no popup/poll — the OAuth dance happens in chat (ManageMcp hands the
  // user an authorize link), so we drop them into a fresh thread with the registry's pre-filled
  // prompt naming the server and its canonical URL.
  //
  // Also the destination for any row the server sent `connectVia: 'assistant-chat'` on, whatever its
  // provider. Such a row need not be in the registry, so the prompt falls back to naming the connector —
  // the same sentence the phones build. The missing-entry refusal still stands for a row routed here by
  // its PROVIDER, where an unresolvable slug really is a broken row rather than a rail we were told about.
  const connectMcp = useCallback(
    (connector: ConnectorStatus) => {
      if (busy) return;
      const firstParty = findFirstPartyMcpBySlug(connector.toolkit);
      if (!firstParty && connector.connectVia !== 'assistant-chat') {
        setActionError("Couldn't start the connect. Please try again.");
        return;
      }
      const connectPrompt = firstParty?.connectPrompt ?? `I want to connect ${connector.name || connector.toolkit}.`;
      setIcloudOpenFor(null); // close a lingering Apple form when connecting an MCP server
      logPageClickBi({ ...CONNECTORS_PAGE, button_name: `connect_${connector.toolkit}` });
      const handoff = chatHandoffLink({ kind: 'new', message: connectPrompt }, basePath);
      void router.push(handoff.route, handoff.path);
    },
    [basePath, busy, logPageClickBi, router],
  );

  const connect = useCallback(
    (connector: ConnectorStatus) =>
      connectorConnectRoute(connector) === 'assistant-chat'
        ? void connectMcp(connector)
        : connectorConnectRoute(connector) === 'native-calendar'
          ? void connectNative(connector)
          : void connectComposio(connector.toolkit),
    [connectNative, connectMcp, connectComposio],
  );

  // Link an additional account on an already-connected row. Native (calendar) rows re-run the native
  // connect flow (which already supports several calendars); Composio rows mint another link with allowMultiple.
  const addAnother = useCallback(
    (connector: ConnectorStatus) =>
      connectorConnectRoute(connector) === 'assistant-chat'
        ? void connectMcp(connector)
        : connectorConnectRoute(connector) === 'native-calendar'
          ? void connectNative(connector)
          : void connectComposio(connector.toolkit, true),
    [connectNative, connectMcp, connectComposio],
  );

  // Disconnect a whole row (no accountId) or a single account of a multiply-linked row. Native rows
  // delete the backing calendar token(s); Composio rows revoke the connected account(s).
  const disconnect = useCallback(
    async (connector: ConnectorStatus, name: string, accountId?: string) => {
      if (busy) return;
      logPageClickBi({ ...CONNECTORS_PAGE, button_name: `disconnect_${connector.toolkit}` });
      // The routines warning rides the WHOLE-connector disconnect only. Removing one account of a
      // multiply-linked row leaves the connector connected, so nothing is deleted and saying otherwise
      // would talk someone out of an action that costs them nothing.
      //
      // Two whole sentences rather than a base string plus an appended clause: a clause bolted onto a
      // translated sentence lands in the wrong place in most languages, and several cannot take it at
      // all without recasting the verb.
      const removesConnector = !accountId || (connector.accounts ?? []).length <= 1;
      const confirmKey = removesConnector ? 'connectors.disconnect.confirmWithRoutines' : 'connectors.disconnect.confirm';
      if (!window.confirm(t(confirmKey, { name, productName }))) return;
      setBusy(accountId ?? connector.toolkit);
      setActionError(null);
      try {
        if (connector.provider === 'native') {
          const ids = accountId ? [accountId] : (connector.accounts ?? []).map((account) => account.id);
          await disconnectCalendarAccounts(ids);
        } else if (connector.provider === 'mcp') {
          // MCP rows carry the McpConnectionService connection ids as their accounts.
          const ids = accountId ? [accountId] : (connector.accounts ?? []).map((account) => account.id);
          for (const id of ids) {
            await deleteMcpConnection(id);
          }
        } else if (connector.provider === 'wix-service') {
          await disconnectConnector(connector.toolkit);
        } else {
          await disconnectConnector(connector.toolkit, accountId);
        }
        await refreshRef.current();
      } catch {
        setActionError(`Couldn't disconnect ${name}. Please try again.`);
        // A row can hold several accounts and they're removed one at a time, so a mid-way failure
        // leaves some already gone — re-read rather than keep rendering rows that no longer exist.
        await refreshRef.current();
      } finally {
        setBusy(null);
      }
    },
    [busy, logPageClickBi, t, productName],
  );

  return {
    busy,
    actionError,
    icloudOpenFor,
    appleId,
    applePassword,
    setAppleId,
    setApplePassword,
    submitICloud,
    connect,
    addAnother,
    disconnect,
  };
}
